import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../services/api';
import {
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Badge,
  InputAdornment,
  Tab,
  Tabs,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Search,
  Send,
  AttachFile,
  EmojiEmotions,
  MoreVert,
  Label,
  Person,
  Schedule,
  CheckCircle,
  RadioButtonUnchecked,
  KeyboardArrowDown,
  ChevronLeft,
  ChevronRight,
  Sort,
  FilterList,
  Group,
  Analytics,
  ExpandLess,
  ExpandMore,
  ContactPage,
  Psychology,
  Assignment as AssignmentIcon,
  Download
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { conversationsAPI, messagesAPI, captainAIAPI } from '../services/api';
import AssignmentDialog from '../components/conversations/AssignmentDialog';
import LabelsDialog from '../components/conversations/LabelsDialog';
import RichTextEditor from '../components/conversations/RichTextEditor';
import VoiceRecorder from '../components/conversations/VoiceRecorder';
import SpeechToTextDialog from '../components/conversations/SpeechToTextDialog';
import SignatureDialog from '../components/conversations/SignatureDialog';
import ParticipantsDialog from '../components/conversations/ParticipantsDialog';
import ConversationMetrics from '../components/conversations/ConversationMetrics';
import CoPilotPanel from '../components/CoPilot/CoPilotPanel';
import CoPilotToggle from '../components/CoPilot/CoPilotToggle';
import { formatMessageTime, formatConversationTime } from '../utils/dateUtils';
import {
  buildExportRows,
  exportConversations,
  type ExportFormat,
  type ExportConversation,
  type ExportMessage
} from '../utils/conversationExport';
import {
  AIStatusIndicator,
  AIConversationControls,
  EscalationBanner,
  AIMessageBubble
} from '../components/AI';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { usePermission } from '../hooks/usePermission';
import {
  TemplateSuggestor,
  QuickTemplateSelector,
  ConversationInitiator
} from '../components/conversations';
import EmailTemplateSuggestor from '../components/conversations/EmailTemplateSuggestor';

interface Conversation {
  id: number;
  display_id: number;
  created_at: string;
  contact: {
    id: number;
    name: string;
    email?: string;
    phone_number?: string;
    avatar_url?: string;
    custom_attributes?: Record<string, any>;
  };
  status: 'open' | 'pending' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  last_activity_at: string;
  ai_enabled: boolean;
  assignee?: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar_url?: string;
  };
  unread_count: number;
  labels: Array<{
    id: number;
    title: string;
    color: string;
    description?: string;
  }>;
  channel: {
    id: number;
    name: string;
    channel_type: string;
  };
  escalated?: boolean;
  escalation_reason?: string;
  escalation_trigger?: string;
  escalated_at?: string;
  escalated_by?: string;
}

interface Attachment {
  id: number;
  file_type: string;
  file_url: string;
  thumb_url?: string;
  file_size?: number;
  direction: string;
  whatsapp_media_id?: string;
  mime_type?: string;
}

interface Message {
  id: number;
  content: string;
  message_type: 'incoming' | 'outgoing' | 'activity';
  content_type?: 'text' | 'file' | 'image' | 'audio' | 'video';
  sender?: {
    name: string;
    avatar_url?: string;
  };
  sender_type?: 'user' | 'agent' | 'ai' | 'bot';
  created_at: string;
  private: boolean;
  content_attributes?: {
    filename?: string;
    file_size?: number;
    file_type?: string;
    file_url?: string;
    mime_type?: string;
  };
  attachments?: Attachment[];
  ai_confidence?: number;
  tools_used?: string[];
  response_time_ms?: number;
  streaming?: boolean;
  metadata?: {
    whatsapp?: any;
    button?: {
      payload?: string;
      text?: string;
      context?: any;
      type?: string;
    };
    interactive?: any;
    bot_flow?: {
      type: "text" | "selection" | "answer";
      question?: string;
      options?: string[];
      selected?: string;
    };
  };
}

const FILE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1').split('/api/')[0];

// ─── colour tokens (single source of truth) ───────────────────────────────────
const C = {
  bg: '#f0f2f5',
  surface: '#ffffff',
  border: '#e5e7eb',
  borderLight: '#f3f4f6',
  primary: '#1f93ff',
  primaryDark: '#0070e0',
  outgoingBg: '#1f93ff',
  outgoingText: '#ffffff',
  incomingBg: '#f0f2f5',
  incomingText: '#111827',
  privateBg: '#fffbeb',
  sidebarBg: '#ffffff',
  headerBg: '#ffffff',
  selectedRow: '#eff6ff',
  selectedRowBorder: '#bfdbfe',
  text1: '#111827',
  text2: '#374151',
  text3: '#6b7280',
  text4: '#9ca3af',
  avatarBg: '#dbeafe',
  avatarText: '#1d4ed8',
  statusOpen: '#16a34a',
  statusPending: '#d97706',
  statusResolved: '#6b7280',
};

const ConversationsPage: React.FC = () => {
  const { id } = useParams();

  const hasViewAll = usePermission('conversations:view_all');
  const hasViewTeam = usePermission('conversations:view_team');
  const hasViewAssigned = usePermission('conversations:view_assigned');
  const canAssign = usePermission('conversations:assign');

  const [selectedTab, setSelectedTab] = useState(0);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: number;
    name: string;
    variables: Record<string, string>;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState('last_activity_at');
  const [uploading, setUploading] = useState(false);
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);
  const [speechToTextDialogOpen, setSpeechToTextDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [userSignature, setUserSignature] = useState('');
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  const [showPreChatData, setShowPreChatData] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const [conversationCounts, setConversationCounts] = useState({ mine: 0, unassigned: 0, all: 0, team: 0 });
  const [lastProcessedMessageId, setLastProcessedMessageId] = useState<number>(0);
  const [copilotEnabled, setCopilotEnabled] = useState(false);
  const [conversationInitiatorOpen, setConversationInitiatorOpen] = useState(false);
  const [sessionState, setSessionState] = useState<'ACTIVE_24H' | 'TEMPLATE_ONLY' | 'NO_RESPONSE' | 'CLOSED'>('TEMPLATE_ONLY');
  const [templateSuggestorRefreshKey, setTemplateSuggestorRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const previousMessageCountRef = useRef(0);
  const firstMessageIdRef = useRef<number | null>(null);

  const availableTabs = React.useMemo(() => {
    const tabs: Array<{ label: string; value: string; scope: string }> = [];
    if (hasViewAssigned || hasViewTeam || hasViewAll) {
      tabs.push({ label: `Mine (${conversationCounts.mine})`, value: 'assigned', scope: 'assigned' });
    }
    if (hasViewTeam || hasViewAll) {
      tabs.push({ label: `Team (${conversationCounts.team})`, value: 'team', scope: 'team' });
    }
    if (hasViewAll) {
      tabs.push({ label: `All (${conversationCounts.all})`, value: 'all', scope: 'all' });
    }
    if (canAssign) {
      tabs.push({ label: `Unassigned (${conversationCounts.unassigned})`, value: 'unassigned', scope: 'unassigned' });
    }
    return tabs;
  }, [hasViewAll, hasViewTeam, hasViewAssigned, canAssign, conversationCounts]);

  useEffect(() => {
    const currentCount = messages.length;
    const previousCount = previousMessageCountRef.current;
    const firstMessageId = messages.length > 0 ? messages[0].id : null;
    const previousFirstMessageId = firstMessageIdRef.current;
    const messagesAddedToEnd = currentCount > previousCount &&
      firstMessageId === previousFirstMessageId &&
      !loadingOlderMessages;
    if (messagesAddedToEnd && shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    previousMessageCountRef.current = currentCount;
    firstMessageIdRef.current = firstMessageId;
  }, [messages, loadingOlderMessages, shouldAutoScroll]);

  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    setShouldAutoScroll(isAtBottom);
    if (element.scrollTop < 50 && hasMoreMessages && !loadingOlderMessages) {
      const previousScrollHeight = element.scrollHeight;
      const previousScrollTop = element.scrollTop;
      handleLoadOlderMessages().then(() => {
        if (messagesContainerRef.current) {
          const newScrollHeight = messagesContainerRef.current.scrollHeight;
          messagesContainerRef.current.scrollTop = previousScrollTop + (newScrollHeight - previousScrollHeight);
        }
      });
    }
  };

  useEffect(() => {
    const savedSignature = localStorage.getItem('userSignature') || '';
    const savedSignatureEnabled = localStorage.getItem('signatureEnabled') === 'true';
    setUserSignature(savedSignature);
    setSignatureEnabled(savedSignatureEnabled);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      const loadParticipants = async () => {
        try {
          const response = await conversationsAPI.getParticipants(selectedConversation.id);
          setParticipants(response.participants || []);
        } catch (err) {
          setParticipants([]);
        }
      };
      loadParticipants();
    }
  }, [selectedConversation]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { per_page: 100 };
      if (filterStatus !== 'all') params.status = filterStatus;
      const currentTab = availableTabs[selectedTab];
      if (currentTab && currentTab.scope !== 'all') params.scope = currentTab.scope;
      const response = await conversationsAPI.getConversations(params);
      setConversations(response.conversations || []);
      const countParams: any = { per_page: 1 };
      if (filterStatus !== 'all') countParams.status = filterStatus;
      const [allResponse, mineResponse, teamResponse, unassignedResponse] = await Promise.all([
        conversationsAPI.getConversations({ ...countParams }),
        conversationsAPI.getConversations({ ...countParams, scope: 'assigned' }),
        (hasViewTeam || hasViewAll) ? conversationsAPI.getConversations({ ...countParams, scope: 'team' }) : Promise.resolve({ total: 0 }),
        canAssign ? conversationsAPI.getConversations({ ...countParams, scope: 'unassigned' }) : Promise.resolve({ total: 0 })
      ]);
      setConversationCounts({
        mine: mineResponse.total ?? 0,
        unassigned: unassignedResponse.total ?? 0,
        all: allResponse.total ?? 0,
        team: teamResponse.total ?? 0
      });
      if (id && response.conversations) {
        const conv = response.conversations.find((c: Conversation) => c.id === parseInt(id));
        setSelectedConversation(conv || null);
      }
    } catch (err) {
      setError('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
    const pollInterval = setInterval(async () => {
      try {
        const params: any = { per_page: 100 };
        if (filterStatus !== 'all') params.status = filterStatus;
        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') params.scope = currentTab.scope;
        const response = await conversationsAPI.getConversations(params);
        setConversations(response.conversations || []);
        const countParams: any = { per_page: 1 };
        if (filterStatus !== 'all') countParams.status = filterStatus;
        const [allResponse, mineResponse, teamResponse, unassignedResponse] = await Promise.all([
          conversationsAPI.getConversations({ ...countParams }),
          conversationsAPI.getConversations({ ...countParams, scope: 'assigned' }),
          (hasViewTeam || hasViewAll) ? conversationsAPI.getConversations({ ...countParams, scope: 'team' }) : Promise.resolve({ total: 0 }),
          canAssign ? conversationsAPI.getConversations({ ...countParams, scope: 'unassigned' }) : Promise.resolve({ total: 0 })
        ]);
        setConversationCounts({
          mine: mineResponse.total ?? 0,
          unassigned: unassignedResponse.total ?? 0,
          all: allResponse.total ?? 0,
          team: teamResponse.total ?? 0
        });
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [id, filterStatus, selectedTab]);

  const loadMessages = useCallback(async () => {
    if (!selectedConversation) return;
    try {
      const initialResponse = await messagesAPI.getMessages(selectedConversation.id, { page: 1, per_page: 1 });
      const total = initialResponse.total || 0;
      setTotalMessages(total);
      if (total <= 50) {
        const response = await messagesAPI.getMessages(selectedConversation.id, { page: 1, per_page: 50 });
        setCurrentPage(1);
        setHasMoreMessages(false);
        setMessages(response.messages || []);
        return;
      }
      const messagesPerPage = 50;
      const toLoad = 50;
      const skip = total - toLoad;
      const startPage = Math.floor(skip / messagesPerPage) + 1;
      const lastPage = Math.ceil(total / messagesPerPage);
      const allMessages = [];
      for (let page = startPage; page <= lastPage; page++) {
        const response = await messagesAPI.getMessages(selectedConversation.id, { page, per_page: 50 });
        allMessages.push(...(response.messages || []));
      }
      const skipWithinFirstPage = skip % messagesPerPage;
      const finalMessages = allMessages.slice(skipWithinFirstPage, skipWithinFirstPage + toLoad);
      setCurrentPage(startPage);
      setHasMoreMessages(skip > 0);
      setMessages(finalMessages);
    } catch (err) {
      setMessages([]);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (selectedConversation) {
      setSelectedTemplate(null);
      setMessageText('');
      setMessages([]);
      setTotalMessages(0);
      setHasMoreMessages(false);
      loadMessages();
      const pollInterval = setInterval(loadMessages, 3000);
      return () => clearInterval(pollInterval);
    }
  }, [selectedConversation, lastProcessedMessageId]);

  const handleSendMessage = async () => {
    if (!selectedConversation) return;
    if (selectedTemplate) {
      try {
        const response = await api.post(`/whatsapp/templates/${selectedTemplate.id}/send`, {
          conversation_id: selectedConversation.id,
          variables: selectedTemplate.variables,
          channel_id: selectedConversation.channel.id
        });
        if (response.data.success) {
          setSelectedTemplate(null);
          setMessageText('');
          loadMessages();
          setConversations(prev => prev.map(conv =>
            conv.id === selectedConversation.id ? { ...conv, last_activity_at: new Date().toISOString() } : conv
          ));
          setTemplateSuggestorRefreshKey(k => k + 1);
        } else {
          throw new Error(response.data.error || 'Failed to send template');
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to send template');
        alert(err.response?.data?.detail || err.message || 'Failed to send template');
      }
      return;
    }
    if (messageText.trim()) {
      try {
        let messageContent = messageText;
        if (signatureEnabled && userSignature.trim()) {
          messageContent = messageText + '\n\n' + userSignature;
        }
        const response = await messagesAPI.sendMessage(selectedConversation.id, {
          content: messageContent,
          message_type: 'outgoing',
          private: false
        });
        setMessages(prev => [...prev, response]);
        setMessageText('');
        setConversations(prev => prev.map(conv =>
          conv.id === selectedConversation.id ? { ...conv, last_activity_at: new Date().toISOString() } : conv
        ));
        setTemplateSuggestorRefreshKey(k => k + 1);
      } catch (err) {
        setError('Failed to send message');
      }
    }
  };

  const handleMessageTextChange = (text: string) => {
    setMessageText(text);
    if (selectedTemplate && text !== messageText) setSelectedTemplate(null);
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedConversation) return;
    try {
      await conversationsAPI.toggleStatus(selectedConversation.id, status);
      setSelectedConversation(prev => prev ? { ...prev, status: status as any } : null);
      setConversations(prev => prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, status: status as any } : conv
      ));
      setAnchorEl(null);
    } catch (err) {
      setError('Failed to change conversation status');
    }
  };

  const handleAssignmentComplete = () => {
    const reload = async () => {
      try {
        const params: any = { per_page: 100 };
        if (filterStatus !== 'all') params.status = filterStatus;
        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') params.scope = currentTab.scope;
        const response = await conversationsAPI.getConversations(params);
        setConversations(response.conversations || []);
        if (selectedConversation && response.conversations) {
          const updatedConv = response.conversations.find((c: Conversation) => c.id === selectedConversation.id);
          if (updatedConv) setSelectedConversation(updatedConv);
        }
      } catch (err) {
        console.error('Failed to reload conversations:', err);
      }
    };
    reload();
  };

  const handleLabelsUpdated = () => handleAssignmentComplete();

  const fetchAllMessagesForConversation = useCallback(async (conversationId: number): Promise<Message[]> => {
    const perPage = 100;
    const first = await messagesAPI.getMessages(conversationId, { page: 1, per_page: perPage });
    const total = first.total ?? 0;
    const allMessages: Message[] = [...(first.messages || [])];
    if (total <= perPage) return allMessages;
    const totalPages = Math.ceil(total / perPage);
    for (let page = 2; page <= totalPages; page++) {
      const res = await messagesAPI.getMessages(conversationId, { page, per_page: perPage });
      allMessages.push(...(res.messages || []));
    }
    return allMessages;
  }, []);

  const toExportConv = (c: Conversation): ExportConversation => ({
    id: c.id, display_id: c.display_id, status: c.status,
    contact: { id: c.contact.id, name: c.contact.name, email: c.contact.email, phone_number: c.contact.phone_number },
    assignee: c.assignee ? { id: c.assignee.id, full_name: c.assignee.full_name } : undefined,
    channel: c.channel ? { name: c.channel.name, channel_type: c.channel.channel_type } : undefined,
    created_at: c.created_at, last_activity_at: c.last_activity_at
  });

  const toExportMsg = (m: Message): ExportMessage => ({
    id: m.id, content: m.content, message_type: m.message_type, content_type: m.content_type,
    sender: m.sender, sender_type: m.sender_type, created_at: m.created_at, private: m.private
  });

  const handleDownloadCurrent = useCallback(async (format: ExportFormat) => {
    if (!selectedConversation) return;
    setDownloadMenuAnchor(null);
    setExporting(true);
    try {
      const msgs = await fetchAllMessagesForConversation(selectedConversation.id);
      const rows = buildExportRows([{ conversation: toExportConv(selectedConversation), messages: msgs.map(toExportMsg) }]);
      const name = `conversation-${selectedConversation.display_id}-${selectedConversation.contact?.name?.replace(/[^a-zA-Z0-9]/g, '_') || selectedConversation.id}`;
      exportConversations(rows, name, format);
    } catch (err) { console.error('Export failed:', err); }
    finally { setExporting(false); }
  }, [selectedConversation, fetchAllMessagesForConversation]);

  const handleDownloadAll = useCallback(async (format: ExportFormat) => {
    setDownloadMenuAnchor(null);
    setExporting(true);
    try {
      const payloads: Array<{ conversation: ExportConversation; messages: ExportMessage[] }> = [];
      for (const conv of conversations) {
        const msgs = await fetchAllMessagesForConversation(conv.id);
        payloads.push({ conversation: toExportConv(conv), messages: msgs.map(toExportMsg) });
      }
      exportConversations(buildExportRows(payloads), `conversations-export-${new Date().toISOString().slice(0, 10)}`, format);
    } catch (err) { console.error('Export all failed:', err); }
    finally { setExporting(false); }
  }, [conversations, fetchAllMessagesForConversation]);

  const handleFileAttachment = async () => {
    if (!selectedConversation) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        setUploading(true);
        const response = await messagesAPI.uploadAttachment(selectedConversation.id, file, false);
        setMessages(prev => [...prev, response]);
        setConversations(prev => prev.map(conv =>
          conv.id === selectedConversation.id ? { ...conv, last_activity_at: new Date().toISOString() } : conv
        ));
      } catch (err) { setError('Failed to upload file'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const handleVoiceRecording = async (audioBlob: Blob) => {
    if (!selectedConversation) return;
    try {
      setUploading(true);
      const audioFile = new File([audioBlob], 'voice-message.webm', { type: 'audio/webm' });
      const response = await messagesAPI.uploadAttachment(selectedConversation.id, audioFile, false);
      setMessages(prev => [...prev, response]);
      setConversations(prev => prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, last_activity_at: new Date().toISOString() } : conv
      ));
    } catch (err) { setError('Failed to send voice message'); }
    finally { setUploading(false); }
  };

  const handleSpeechToTextComplete = (transcript: string) => {
    setMessageText(prev => { const t = prev.trim(); return t ? `${t}\n\n${transcript}` : transcript; });
    setSpeechToTextDialogOpen(false);
  };

  const handleSignatureSave = (signature: string, enabled: boolean) => {
    setUserSignature(signature);
    setSignatureEnabled(enabled);
    localStorage.setItem('userSignature', signature);
    localStorage.setItem('signatureEnabled', enabled.toString());
  };

  const handleParticipantsUpdated = () => {
    if (selectedConversation) {
      conversationsAPI.getParticipants(selectedConversation.id)
        .then(r => setParticipants(r.participants || []))
        .catch(() => {});
    }
    handleAssignmentComplete();
  };

  const handleAISuggestionSelect = (suggestion: string) => {
    setMessageText(prev => { const t = prev.trim(); return t ? `${t}\n\n${suggestion}` : suggestion; });
  };

  const handleAiToggle = async () => {
    if (!selectedConversation) return;
    try {
      const response = await conversationsAPI.toggleAI(selectedConversation.id);
      setSelectedConversation(prev => prev ? { ...prev, ai_enabled: response.ai_enabled } : null);
      setConversations(prev => prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, ai_enabled: response.ai_enabled } : conv
      ));
    } catch (err) { setError('Failed to toggle AI assistant'); }
  };

  const isAiEnabled = selectedConversation?.ai_enabled || false;

  const handleAiAutoResponse = async (conversationId: number, incomingMessage: Message) => {
    try {
      const aiResponse = await captainAIAPI.generateAutoResponse(conversationId, {
        message: incomingMessage.content, message_id: incomingMessage.id
      });
      if (aiResponse?.response) {
        await messagesAPI.sendMessage(conversationId, {
          content: aiResponse.response, message_type: 'outgoing', private: false
        });
      }
    } catch (err) { /* fail silently */ }
  };

  const handleLoadOlderMessages = async () => {
    if (!selectedConversation || loadingOlderMessages || !hasMoreMessages) return;
    setLoadingOlderMessages(true);
    try {
      const currentlyLoaded = messages.length;
      const alreadySkipped = totalMessages - currentlyLoaded;
      const toLoad = Math.min(50, alreadySkipped);
      const newSkip = alreadySkipped - toLoad;
      const page = Math.floor(newSkip / 50) + 1;
      const skipWithinPage = newSkip % 50;
      const response = await messagesAPI.getMessages(selectedConversation.id, { page, per_page: 50 });
      let olderMessages = response.messages || [];
      if (skipWithinPage > 0 || toLoad < 50) {
        olderMessages = olderMessages.slice(skipWithinPage, skipWithinPage + toLoad);
      }
      if (olderMessages.length > 0) {
        setMessages(prev => [...olderMessages, ...prev]);
        setCurrentPage(page);
        setHasMoreMessages(newSkip > 0);
      }
    } catch (err) { console.error('Failed to load older messages:', err); }
    finally { setLoadingOlderMessages(false); }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'success';
      case 'pending': return 'warning';
      case 'resolved': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#3b82f6';
      case 'low': return '#22c55e';
      default: return '#9ca3af';
    }
  };

  const getChannelIcon = (type: string) => {
    const iconStyle = { width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle' };
    const getIconPath = (t: string) => {
      switch (t) {
        case 'whatsapp': return '/icons/whatsapp.svg';
        case 'email': return '/icons/email.svg';
        case 'website': return '/icons/website.svg';
        case 'api': return '/icons/api.svg';
        case 'facebook': return '/icons/facebook.svg';
        case 'twitter': return '/icons/twitter.svg';
        case 'instagram': return '/icons/instagram.svg';
        case 'telegram': return '/icons/telegram.svg';
        case 'sms': return '/icons/sms.svg';
        case 'slack': return '/icons/slack.svg';
        case 'custom_gpt': return '/icons/custom_gpt.svg';
        default: return '/icons/api.svg';
      }
    };
    return <img src={getIconPath(type)} alt={`${type} channel`} style={iconStyle} />;
  };

  const hasPreChatFormData = (conversation: Conversation) =>
    !!(conversation.contact.custom_attributes && Object.keys(conversation.contact.custom_attributes).length > 0);

  const formatPreChatFormData = (customAttributes: Record<string, any>) => {
    if (!customAttributes) return [];
    return Object.entries(customAttributes)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        value: typeof value === 'object' ? JSON.stringify(value) : String(value)
      }));
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderAttachment = (message: Message) => {
    if (message.attachments && message.attachments.length > 0) {
      return (
        <Box sx={{ mt: 1 }}>
          {message.attachments.map((attachment) => {
            const { file_type, file_url, file_size } = attachment;
            const filename = file_url.split('/').pop() || 'file';
            if (file_type === 'image') return (
              <Box key={attachment.id} sx={{ mb: 1 }}>
                <img src={`${FILE_BASE}${file_url}`} alt={filename}
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer' }}
                  onClick={() => window.open(`${FILE_BASE}${file_url}`, '_blank')} />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                  {filename} ({formatFileSize(file_size)})
                </Typography>
              </Box>
            );
            if (file_type === 'audio') return (
              <Box key={attachment.id} sx={{ mb: 1 }}>
                <audio controls style={{ width: '250px' }} src={`${FILE_BASE}${file_url}`} />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                  🎵 {filename} ({formatFileSize(file_size)})
                </Typography>
              </Box>
            );
            if (file_type === 'video') return (
              <Box key={attachment.id} sx={{ mb: 1 }}>
                <video controls style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px' }} src={`${FILE_BASE}${file_url}`} />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                  🎥 {filename} ({formatFileSize(file_size)})
                </Typography>
              </Box>
            );
            const getFileIcon = (t?: string) => ({ document: '📄', audio: '🎵', video: '🎥', image: '🖼️' }[t || ''] || '📎');
            return (
              <Box key={attachment.id} sx={{ mb: 1 }}>
                <Button variant="outlined" size="small" startIcon={<span>{getFileIcon(file_type)}</span>}
                  onClick={() => window.open(`${FILE_BASE}${file_url}`, '_blank')} sx={{ textTransform: 'none' }}>
                  {filename}
                </Button>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>{formatFileSize(file_size)}</Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    if (!message.content_attributes?.file_url || message.content_type === 'text') return null;
    const { filename, file_size, file_type, file_url } = message.content_attributes;
    if (message.content_type === 'image') return (
      <Box sx={{ mt: 1 }}>
        <img src={`${FILE_BASE}${file_url}`} alt={filename}
          style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer' }}
          onClick={() => window.open(`${FILE_BASE}${file_url}`, '_blank')} />
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
          {filename} ({formatFileSize(file_size)})
        </Typography>
      </Box>
    );
    if (message.content_type === 'audio') return (
      <Box sx={{ mt: 1 }}>
        <audio controls style={{ width: '250px' }} src={`${FILE_BASE}${file_url}`} />
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>🎵 {filename}</Typography>
      </Box>
    );
    if (message.content_type === 'video') return (
      <Box sx={{ mt: 1 }}>
        <video controls style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px' }} src={`${FILE_BASE}${file_url}`} />
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>🎥 {filename}</Typography>
      </Box>
    );
    const getFileIcon = (t?: string) => ({ document: '📄', audio: '🎵', video: '🎥' }[t || ''] || '📎');
    return (
      <Box sx={{ mt: 1 }}>
        <Button variant="outlined" size="small" startIcon={<span>{getFileIcon(file_type)}</span>}
          onClick={() => window.open(`${FILE_BASE}${file_url}`, '_blank')} sx={{ textTransform: 'none' }}>
          {filename}
        </Button>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>{formatFileSize(file_size)}</Typography>
      </Box>
    );
  };

  const renderButtonResponse = (message: Message) => {
    const metadata = message.metadata || {};
    const botFlow = metadata.bot_flow;
    if (botFlow) {
      if (botFlow.type === "selection") return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip label="✓ Selected" size="small" color="success" variant="filled" />
          <Chip label={botFlow.selected || "Option selected"} size="small" color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
        </Box>
      );
      if (botFlow.type === "answer") return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>{botFlow.selected || botFlow.question || message.content}</Typography>
        </Box>
      );
    }
    if (metadata.interactive?.type === "interactive_list") return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip label="📋 Selection" size="small" color="secondary" variant="outlined" />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{metadata.interactive.title}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>ID: {metadata.interactive.list_id}</Typography>
      </Box>
    );
    if (metadata.button) return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip label="🔘 Button Click" size="small" color="primary" variant="outlined" />
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{metadata.button.text}</Typography>
      </Box>
    );
    return null;
  };

  const filteredConversations = useMemo(() => {
    return conversations.filter(conv => {
      const matchesSearch = conv.contact.name.toLowerCase().includes(searchText.toLowerCase()) ||
        conv.contact.email?.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = filterStatus === 'all' || conv.status === filterStatus;
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      let result = 0;
      switch (sortBy) {
        case 'last_activity_at': result = new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime(); break;
        case 'id': result = (b.id || 0) - (a.id || 0); break;
        case 'created_at': result = (b.created_at ? new Date(b.created_at).getTime() : 0) - (a.created_at ? new Date(a.created_at).getTime() : 0); break;
        case 'contact_name': result = a.contact.name.localeCompare(b.contact.name); break;
        case 'status': result = a.status.localeCompare(b.status); break;
        case 'priority': {
          const o: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
          result = (o[a.priority] ?? 999) - (o[b.priority] ?? 999); break;
        }
        default: result = 0;
      }
      if (result === 0 && sortBy !== 'last_activity_at') {
        result = new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
      }
      return result;
    });
  }, [conversations, searchText, filterStatus, sortBy]);

  if (error) {
    return (
      <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // ─── SIDEBAR WIDTH ────────────────────────────────────────────────────────────
  const SIDEBAR_W = sidebarCollapsed ? 56 : 320;

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      overflow: 'hidden',
      bgcolor: C.bg,
      gap: 0,
    }}>

      {/* ═══════════════════════════════════════════════════════════════════════
          LEFT SIDEBAR — conversation list
      ════════════════════════════════════════════════════════════════════════ */}
      <Box sx={{
        width: SIDEBAR_W,
        minWidth: SIDEBAR_W,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: C.sidebarBg,
        borderRight: `1px solid ${C.border}`,
        transition: 'width 0.25s ease, min-width 0.25s ease',
        overflow: 'hidden',
      }}>

        {/* Sidebar header */}
        <Box sx={{
          px: sidebarCollapsed ? 1 : 2,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${C.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56,
        }}>
          {!sidebarCollapsed && (
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: C.text1, letterSpacing: '-0.01em' }}>
              Conversations
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: sidebarCollapsed ? 'auto' : 0 }}>
            {!sidebarCollapsed && (
              <>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={(e) => setDownloadMenuAnchor(e.currentTarget)} disabled={exporting}
                    sx={{ color: C.text3, '&:hover': { color: C.text1, bgcolor: C.borderLight } }}>
                    {exporting ? <CircularProgress size={16} /> : <Download sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sort">
                  <IconButton size="small" onClick={(e) => setSortAnchorEl(e.currentTarget)}
                    sx={{ color: C.text3, '&:hover': { color: C.text1, bgcolor: C.borderLight } }}>
                    <Sort sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={() => setSortAnchorEl(null)}>
                  {[
                    { value: 'id', label: 'Conversation ID' },
                    { value: 'last_activity_at', label: 'Last Activity' },
                    { value: 'created_at', label: 'Created Date' },
                    { value: 'contact_name', label: 'Contact Name' },
                    { value: 'status', label: 'Status' },
                    { value: 'priority', label: 'Priority' },
                  ].map(opt => (
                    <MenuItem key={opt.value} selected={sortBy === opt.value}
                      onClick={() => { setSortBy(opt.value); setSortAnchorEl(null); }}
                      sx={{ fontSize: '0.875rem' }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            <Tooltip title={sidebarCollapsed ? 'Expand' : 'Collapse'}>
              <IconButton size="small" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                sx={{ color: C.text3, '&:hover': { color: C.text1, bgcolor: C.borderLight } }}>
                {sidebarCollapsed ? <ChevronRight sx={{ fontSize: 18 }} /> : <ChevronLeft sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {!sidebarCollapsed && (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: `1px solid ${C.borderLight}` }}>
              <Tabs
                value={selectedTab}
                onChange={(_, v) => setSelectedTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 40,
                  px: 1,
                  '& .MuiTab-root': {
                    minHeight: 40,
                    minWidth: 'auto',
                    px: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: C.text3,
                    '&.Mui-selected': { color: C.primary, fontWeight: 700 },
                  },
                  '& .MuiTabs-indicator': { backgroundColor: C.primary, height: 2 },
                }}>
                {availableTabs.map((tab, index) => (
                  <Tab key={tab.value} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            {/* Search + filter */}
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.borderLight}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                fullWidth size="small" placeholder="Search…" value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: C.text4 }} /></InputAdornment>,
                  sx: { fontSize: '0.8rem', borderRadius: 2, bgcolor: C.bg }
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <FormControl size="small" fullWidth>
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.8rem', borderRadius: 2, bgcolor: C.bg }}>
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="snoozed">Snoozed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {/* Conversation rows */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {filteredConversations.map((conversation) => {
            const isSelected = selectedConversation?.id === conversation.id;
            return (
              <Box
                key={conversation.id}
                onClick={() => setSelectedConversation(conversation)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  px: sidebarCollapsed ? 1 : 2,
                  py: sidebarCollapsed ? 1 : 1.5,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${C.borderLight}`,
                  bgcolor: isSelected ? C.selectedRow : 'transparent',
                  borderLeft: isSelected ? `3px solid ${C.primary}` : '3px solid transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: isSelected ? C.selectedRow : '#f9fafb' },
                }}
              >
                <Badge badgeContent={conversation.unread_count} color="error"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                  <Avatar sx={{
                    width: 36, height: 36, fontSize: '0.875rem', fontWeight: 700,
                    bgcolor: isSelected ? C.primary : C.avatarBg,
                    color: isSelected ? '#fff' : C.avatarText,
                    flexShrink: 0,
                  }}>
                    {conversation.contact.name.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>

                {!sidebarCollapsed && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: C.text1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {conversation.contact.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {getChannelIcon(conversation.channel.channel_type)}
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: getPriorityColor(conversation.priority), flexShrink: 0 }} />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={conversation.status}
                        size="small"
                        sx={{
                          height: 18, fontSize: '0.65rem', fontWeight: 600,
                          bgcolor: conversation.status === 'open' ? '#dcfce7' : conversation.status === 'pending' ? '#fef3c7' : '#f3f4f6',
                          color: conversation.status === 'open' ? '#15803d' : conversation.status === 'pending' ? '#92400e' : '#6b7280',
                          border: 'none',
                          '& .MuiChip-label': { px: 0.75 }
                        }}
                      />
                      {conversation.labels.slice(0, 2).map((label) => (
                        <Chip key={label.id} label={label.title} size="small"
                          sx={{ height: 18, fontSize: '0.65rem', bgcolor: label.color, color: '#fff', border: 'none', '& .MuiChip-label': { px: 0.75 } }} />
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: C.text4 }}>
                        #{conversation.display_id}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: C.text4 }}>
                        {formatMessageTime(conversation.last_activity_at)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          RIGHT PANEL — conversation detail
      ════════════════════════════════════════════════════════════════════════ */}
      {selectedConversation ? (
        <Box sx={{ flex: 1, display: 'flex', minWidth: 0, overflow: 'hidden' }}>

          {/* Main chat column */}
          <Box sx={{
            flex: copilotEnabled ? '1 1 60%' : 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
            bgcolor: C.surface,
          }}>

            {/* ── Header ── */}
            <Box sx={{
              px: 2.5, py: 1.5,
              borderBottom: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              bgcolor: C.headerBg,
              flexShrink: 0,
            }}>
              <Avatar sx={{ width: 38, height: 38, bgcolor: C.primary, color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}>
                {selectedConversation.contact.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: C.text1, lineHeight: 1.3 }} noWrap>
                  {selectedConversation.contact.name}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: C.text3, lineHeight: 1.3 }} noWrap>
                  {selectedConversation.contact.email || selectedConversation.contact.phone_number} · #{selectedConversation.display_id}
                </Typography>
              </Box>

              {/* Co-Pilot toggle */}
              <CoPilotToggle enabled={copilotEnabled} onChange={setCopilotEnabled} />

              {/* Action buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                <Tooltip title="Assign">
                  <Button size="small" startIcon={<Person sx={{ fontSize: 15 }} />}
                    onClick={() => setAssignmentDialogOpen(true)}
                    sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: C.text2,
                      textTransform: 'none', px: 1, py: 0.5, minWidth: 'auto',
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: C.borderLight }
                    }}>
                    {selectedConversation.assignee ? selectedConversation.assignee.first_name : 'Assign'}
                  </Button>
                </Tooltip>

                {[
                  { title: 'Labels', icon: <Label sx={{ fontSize: 17 }} />, onClick: () => setLabelsDialogOpen(true) },
                  { title: 'Participants', icon: <Group sx={{ fontSize: 17 }} />, onClick: () => setParticipantsDialogOpen(true) },
                  {
                    title: selectedConversation.status === 'resolved' ? 'Resolved' : 'Resolve',
                    icon: <CheckCircle sx={{ fontSize: 17, color: selectedConversation.status === 'resolved' ? C.statusOpen : undefined }} />,
                    onClick: () => handleStatusChange('resolved')
                  },
                  { title: 'Analytics', icon: <Analytics sx={{ fontSize: 17 }} />, onClick: () => setMetricsDialogOpen(true) },
                  {
                    title: 'Download', icon: exporting ? <CircularProgress size={17} /> : <Download sx={{ fontSize: 17 }} />,
                    onClick: (e: any) => setDownloadMenuAnchor(e.currentTarget),
                    disabled: exporting
                  },
                ].map((btn, i) => (
                  <Tooltip key={i} title={btn.title}>
                    <span>
                      <IconButton size="small" onClick={btn.onClick} disabled={btn.disabled}
                        sx={{ color: C.text3, borderRadius: 1.5, '&:hover': { color: C.text1, bgcolor: C.borderLight } }}>
                        {btn.icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                ))}

                <AIConversationControls
                  conversationId={selectedConversation.id}
                  aiEnabled={isAiEnabled}
                  escalated={selectedConversation.escalated || false}
                  onAIToggle={(enabled) => {
                    setSelectedConversation(prev => prev ? { ...prev, ai_enabled: enabled } : null);
                    loadConversations();
                  }}
                  onEscalate={() => loadConversations()}
                />

                {hasPreChatFormData(selectedConversation) && (
                  <Tooltip title="Pre-chat Info">
                    <IconButton size="small" onClick={() => setShowPreChatData(!showPreChatData)}
                      sx={{ color: showPreChatData ? C.primary : C.text3, borderRadius: 1.5, '&:hover': { bgcolor: C.borderLight } }}>
                      <ContactPage sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="More">
                  <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ color: C.text3, borderRadius: 1.5, '&:hover': { color: C.text1, bgcolor: C.borderLight } }}>
                    <MoreVert sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* ── AI Status Bar ── */}
            <Box sx={{ px: 2.5, py: 1, borderBottom: `1px solid ${C.borderLight}`, bgcolor: '#fafafa', flexShrink: 0 }}>
              <AIStatusIndicator
                aiEnabled={isAiEnabled}
                escalated={selectedConversation.escalated}
                escalationReason={selectedConversation.escalation_reason}
                isProcessing={false}
                showDetails={true}
              />
            </Box>

            {/* ── Escalation Banner ── */}
            {selectedConversation.escalated && (
              <Box sx={{ px: 2.5, py: 1, flexShrink: 0 }}>
                <EscalationBanner
                  conversationId={selectedConversation.id}
                  escalated={selectedConversation.escalated}
                  escalationReason={selectedConversation.escalation_reason}
                  escalationTrigger={selectedConversation.escalation_trigger}
                  escalatedAt={selectedConversation.escalated_at ? new Date(selectedConversation.escalated_at) : undefined}
                  escalatedBy={selectedConversation.escalated_by}
                  assignedAgent={selectedConversation.assignee}
                  onResolve={() => loadConversations()}
                  onToggleAI={() => {
                    setSelectedConversation(prev => prev ? { ...prev, ai_enabled: true } : null);
                    loadConversations();
                  }}
                />
              </Box>
            )}

            {/* ── Pre-chat Data ── */}
            {hasPreChatFormData(selectedConversation) && (
              <Collapse in={showPreChatData}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.borderLight}`, bgcolor: '#fafafa', flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ContactPage sx={{ fontSize: 16, color: C.primary }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: C.text1 }}>Pre-chat Form</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
                    {formatPreChatFormData(selectedConversation.contact.custom_attributes || {}).map((item) => (
                      <Box key={item.key}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: C.text4, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: C.text2, wordBreak: 'break-word' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Collapse>
            )}

            {/* ── Messages area ── */}
            <Box
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              sx={{
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: 0,
                overflowY: 'auto',
                minHeight: 0,
                px: 2.5,
                py: 2,
                bgcolor: C.bg,
              }}
            >
              {loadingOlderMessages && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              )}

              {messages.map((message) => {
                if (message.sender_type === 'ai' || message.sender_type === 'bot') {
                  return (
                    <AIMessageBubble
                      key={message.id}
                      content={message.content}
                      confidence={message.ai_confidence}
                      toolsUsed={message.tools_used}
                      responseTimeMs={message.response_time_ms}
                      isStreaming={message.streaming || false}
                      timestamp={new Date(message.created_at)}
                      showMetadata={true}
                    />
                  );
                }

                const isOutgoing = message.message_type === 'outgoing';
                const isActivity = message.message_type === 'activity';

                if (isActivity) {
                  return (
                    <Box key={message.id} sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                      <Typography sx={{
                        fontSize: '0.72rem', color: C.text4, bgcolor: C.borderLight,
                        px: 1.5, py: 0.5, borderRadius: 10
                      }}>
                        {message.content}
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <Box key={message.id} sx={{
                    mb: 1.5,
                    display: 'flex',
                    justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                  }}>
                    {!isOutgoing && (
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: C.avatarBg, color: C.avatarText, mr: 1, mt: 0.5, flexShrink: 0 }}>
                        {message.sender?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                    )}
                    <Box sx={{
                      maxWidth: '68%',
                      px: 1.75,
                      py: 1.25,
                      borderRadius: isOutgoing ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                      bgcolor: isOutgoing ? C.outgoingBg : message.private ? C.privateBg : C.surface,
                      color: isOutgoing ? C.outgoingText : C.incomingText,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
                      border: !isOutgoing && !message.private ? `1px solid ${C.borderLight}` : 'none',
                    }}>
                      {message.private && (
                        <Typography sx={{ fontSize: '0.7rem', fontStyle: 'italic', mb: 0.5, color: '#92400e', fontWeight: 600 }}>
                          🔒 Private Note
                        </Typography>
                      )}
                      {message.metadata?.bot_flow || message.metadata?.button || message.metadata?.interactive
                        ? renderButtonResponse(message)
                        : (
                          <MarkdownRenderer
                            content={message.content}
                            sx={{
                              fontSize: '0.875rem',
                              lineHeight: 1.55,
                              color: isOutgoing ? C.outgoingText : C.incomingText,
                              '& a': { color: isOutgoing ? '#bfdbfe' : C.primary },
                              '& code': { backgroundColor: isOutgoing ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', borderRadius: 4, px: '3px' },
                              '& pre': { backgroundColor: isOutgoing ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)', borderRadius: 6, p: 1, overflowX: 'auto' },
                              '& p': { m: 0 },
                            }}
                          />
                        )}
                      {renderAttachment(message)}
                      <Typography sx={{ fontSize: '0.68rem', mt: 0.75, opacity: 0.65, textAlign: isOutgoing ? 'right' : 'left' }}>
                        {formatMessageTime(message.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            {/* ── Template suggestors ── */}
            {selectedConversation?.channel?.channel_type === 'whatsapp' && (
              <Box sx={{ px: 2, py: 1, bgcolor: C.surface, borderTop: `1px solid ${C.borderLight}`, flexShrink: 0 }}>
                <TemplateSuggestor
                  conversationId={selectedConversation.id}
                  contactPhone={selectedConversation.contact?.phone_number}
                  contact={selectedConversation?.contact}
                  refreshTrigger={templateSuggestorRefreshKey}
                  onSelectTemplate={(template, variables) => {
                    setSelectedTemplate({ id: template.id, name: template.name, variables });
                    const bodyText = template.components?.body;
                    if (bodyText) {
                      let preview = bodyText;
                      Object.entries(variables).forEach(([key, value]) => {
                        preview = preview.replace(`{{${key}}}`, value);
                      });
                      setMessageText(preview);
                    } else {
                      setMessageText(`[Template: ${template.name}]`);
                    }
                  }}
                  compact
                />
              </Box>
            )}
            {selectedConversation?.channel?.channel_type === 'email' && (
              <Box sx={{ px: 2, py: 1, bgcolor: C.surface, borderTop: `1px solid ${C.borderLight}`, flexShrink: 0 }}>
                <EmailTemplateSuggestor conversationId={selectedConversation.id} onSent={() => loadMessages()} />
              </Box>
            )}

            {/* ── Message input ── */}
            <Box sx={{ borderTop: `1px solid ${C.border}`, bgcolor: C.surface, flexShrink: 0 }}>
              <RichTextEditor
                value={messageText}
                onChange={handleMessageTextChange}
                onSend={handleSendMessage}
                onAttachFile={handleFileAttachment}
                onStartRecording={() => setVoiceRecorderOpen(true)}
                onStartSpeechToText={() => setSpeechToTextDialogOpen(true)}
                onOpenSignatureDialog={() => setSignatureDialogOpen(true)}
                placeholder="Type your message…"
                disabled={loading || uploading}
                showSignatureButton={true}
              />
            </Box>
          </Box>

          {/* Co-Pilot Panel */}
          {copilotEnabled && (
            <Box sx={{
              flex: '0 0 380px',
              minWidth: 320,
              borderLeft: `1px solid ${C.border}`,
              bgcolor: C.surface,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <CoPilotPanel conversationId={selectedConversation.id} />
            </Box>
          )}
        </Box>
      ) : (
        /* Empty state */
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: C.bg,
          gap: 1.5,
        }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            bgcolor: C.borderLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircle sx={{ fontSize: 32, color: C.text4 }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: C.text2 }}>
            Select a conversation
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: C.text4 }}>
            Choose from the list to start messaging
          </Typography>
        </Box>
      )}

      {/* ── Context Menu ── */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {[
          { label: 'Snooze', icon: <Schedule sx={{ mr: 1, fontSize: 18 }} />, value: 'snoozed' },
          { label: 'Mark as pending', icon: <RadioButtonUnchecked sx={{ mr: 1, fontSize: 18 }} />, value: 'pending' },
          { label: 'Reopen', icon: <RadioButtonUnchecked sx={{ mr: 1, fontSize: 18 }} />, value: 'open' },
          { label: 'Resolve', icon: <CheckCircle sx={{ mr: 1, fontSize: 18 }} />, value: 'resolved' },
        ].map(item => (
          <MenuItem key={item.value} onClick={() => handleStatusChange(item.value)} sx={{ fontSize: '0.875rem' }}>
            {item.icon}{item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* ── Download Menu ── */}
      <Menu anchorEl={downloadMenuAnchor} open={Boolean(downloadMenuAnchor)} onClose={() => setDownloadMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 700, fontSize: '0.75rem' }}>This conversation</MenuItem>
        {['csv', 'excel', 'json'].map(f => (
          <MenuItem key={f} onClick={() => handleDownloadCurrent(f as ExportFormat)}
            disabled={!selectedConversation || exporting} sx={{ fontSize: '0.875rem', textTransform: 'uppercase' }}>
            {f}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 700, fontSize: '0.75rem' }}>All conversations</MenuItem>
        {['csv', 'excel', 'json'].map(f => (
          <MenuItem key={f} onClick={() => handleDownloadAll(f as ExportFormat)}
            disabled={exporting} sx={{ fontSize: '0.875rem', textTransform: 'uppercase' }}>
            {f}
          </MenuItem>
        ))}
      </Menu>

      {/* ── Dialogs ── */}
      <AssignmentDialog open={assignmentDialogOpen} onClose={() => setAssignmentDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentAssignee={selectedConversation?.assignee} onAssigned={handleAssignmentComplete} />

      <LabelsDialog open={labelsDialogOpen} onClose={() => setLabelsDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentLabels={selectedConversation?.labels || []} onLabelsUpdated={handleLabelsUpdated} />

      <VoiceRecorder open={voiceRecorderOpen} onClose={() => setVoiceRecorderOpen(false)} onSendRecording={handleVoiceRecording} />

      <SpeechToTextDialog open={speechToTextDialogOpen} onClose={() => setSpeechToTextDialogOpen(false)}
        onTranscriptComplete={handleSpeechToTextComplete} />

      <SignatureDialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}
        onSaveSignature={handleSignatureSave} currentSignature={userSignature} signatureEnabled={signatureEnabled} />

      <ParticipantsDialog open={participantsDialogOpen} onClose={() => setParticipantsDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentParticipants={participants} onParticipantsUpdated={handleParticipantsUpdated} />

      {selectedConversation && (
        <ConversationMetrics open={metricsDialogOpen} onClose={() => setMetricsDialogOpen(false)}
          conversationId={selectedConversation.id} conversation={selectedConversation} />
      )}

      <ConversationInitiator open={conversationInitiatorOpen} onClose={() => setConversationInitiatorOpen(false)}
        onConversationCreated={(newConversationId) => {
          loadConversations();
          window.location.href = `/conversations/${newConversationId}`;
        }} />
    </Box>
  );
};

export default ConversationsPage;
